import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Building2, CreditCard, Users, TrendingUp, DollarSign, Clock,
  Ban, CheckCircle, Plus, Pencil, Trash2, X, ChevronDown, Activity,
  ArrowUpRight, ArrowDownRight, RefreshCw,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);
const fmtFull = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_COLORS = {
  active: { bg: '#dcfce7', color: '#16a34a', label: 'Active' },
  trialing: { bg: '#dbeafe', color: '#2563eb', label: 'Trial' },
  pending: { bg: '#fef3c7', color: '#d97706', label: 'Pending' },
  past_due: { bg: '#fef3c7', color: '#d97706', label: 'Past Due' },
  cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' },
  expired: { bg: '#f1f5f9', color: '#64748b', label: 'Expired' },
};

// ============================================================
// STAT CARD
// ============================================================
function StatCard({ icon: Icon, label, value, sub, color, trend }) {
  return (
    <div className="saas-stat">
      <div className="saas-stat-icon" style={{ background: color }}>
        <Icon size={18} />
      </div>
      <div className="saas-stat-text">
        <span className="saas-stat-label">{label}</span>
        <span className="saas-stat-value">{value}</span>
        {sub && <span className="saas-stat-sub">{sub}</span>}
      </div>
      {trend !== undefined && (
        <span className={`saas-stat-trend ${trend >= 0 ? 'up' : 'down'}`}>
          {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(trend)}
        </span>
      )}
    </div>
  );
}

// ============================================================
// PLAN CARD
// ============================================================
function PlanCard({ plan, onEdit, onDelete }) {
  const features = (() => { try { return JSON.parse(plan.features || '[]'); } catch { return []; } })();
  return (
    <div className={`saas-plan-card ${plan.is_active ? '' : 'inactive'}`}>
      <div className="saas-plan-header">
        <h4>{plan.name}</h4>
        <div className="saas-plan-price">
          <span className="saas-plan-amount">{fmt(plan.price_monthly)}</span>
          <span className="saas-plan-period">/mo</span>
        </div>
      </div>
      <p className="saas-plan-desc">{plan.description}</p>
      <div className="saas-plan-limits">
        <div><Users size={12} /> {plan.max_users} users</div>
        <div><Building2 size={12} /> {plan.max_companies} {plan.max_companies === 1 ? 'company' : 'companies'}</div>
        <div><CreditCard size={12} /> {plan.max_invoices_month === -1 ? 'Unlimited' : plan.max_invoices_month} invoices/mo</div>
      </div>
      {features.length > 0 && (
        <ul className="saas-plan-features">
          {features.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
      <div className="saas-plan-yearly">
        Yearly: <strong>{fmt(plan.price_yearly)}/yr</strong>
        {plan.price_yearly > 0 && plan.price_monthly > 0 && (
          <span className="saas-plan-save">
            Save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%
          </span>
        )}
      </div>
      <div className="saas-plan-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => onEdit(plan)}><Pencil size={12} /> Edit</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onDelete(plan)}><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

// ============================================================
// PLAN FORM MODAL
// ============================================================
function PlanFormModal({ plan, onClose, onSave }) {
  const [form, setForm] = useState(plan || {
    name: '', slug: '', description: '', price_monthly: 0, price_yearly: 0,
    max_users: 5, max_companies: 1, max_invoices_month: 100, max_storage_mb: 500,
    features: [], is_active: true, sort_order: 0,
  });
  const [featureText, setFeatureText] = useState(
    (() => { try { return JSON.parse(plan?.features || '[]').join('\n'); } catch { return ''; } })()
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, features: featureText.split('\n').filter(Boolean) };
      if (!data.slug) data.slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (plan?.id) { await subscriptionsAPI.updatePlan(plan.id, data); toast.success('Plan updated'); }
      else { await subscriptionsAPI.createPlan(data); toast.success('Plan created'); }
      onSave();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal saas-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{plan?.id ? 'Edit Plan' : 'New Plan'}</h3>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group"><label>Plan Name *</label><input className="form-control" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div className="form-group"><label>Slug</label><input className="form-control" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="auto-generated" /></div>
            </div>
            <div className="form-group"><label>Description</label><input className="form-control" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="form-grid">
              <div className="form-group"><label>Monthly Price ($)</label><input className="form-control" type="number" step="0.01" min="0" value={form.price_monthly} onChange={e => setForm({...form, price_monthly: parseFloat(e.target.value) || 0})} /></div>
              <div className="form-group"><label>Yearly Price ($)</label><input className="form-control" type="number" step="0.01" min="0" value={form.price_yearly} onChange={e => setForm({...form, price_yearly: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label>Max Users</label><input className="form-control" type="number" min="1" value={form.max_users} onChange={e => setForm({...form, max_users: parseInt(e.target.value) || 1})} /></div>
              <div className="form-group"><label>Max Companies</label><input className="form-control" type="number" min="1" value={form.max_companies} onChange={e => setForm({...form, max_companies: parseInt(e.target.value) || 1})} /></div>
              <div className="form-group"><label>Max Invoices/Month (-1 = unlimited)</label><input className="form-control" type="number" min="-1" value={form.max_invoices_month} onChange={e => setForm({...form, max_invoices_month: parseInt(e.target.value) || 100})} /></div>
              <div className="form-group"><label>Max Storage (MB)</label><input className="form-control" type="number" min="100" value={form.max_storage_mb} onChange={e => setForm({...form, max_storage_mb: parseInt(e.target.value) || 500})} /></div>
            </div>
            <div className="form-group"><label>Features (one per line)</label><textarea className="form-control" rows={4} value={featureText} onChange={e => setFeatureText(e.target.value)} placeholder={"Full accounting\nInvoicing\nBasic reports"} /></div>
            <div className="form-group" style={{display:'flex',gap:8,alignItems:'center'}}>
              <input type="checkbox" id="plan-active" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
              <label htmlFor="plan-active" style={{margin:0}}>Active</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// ASSIGN SUBSCRIPTION MODAL
// ============================================================
function AssignModal({ company, plans, onClose, onSave }) {
  const [form, setForm] = useState({
    company_id: company.id,
    plan_id: plans.find(p => p.slug === 'free-trial')?.id || plans[0]?.id || '',
    billing_cycle: 'monthly',
    trial_days: 14,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await subscriptionsAPI.assign(form);
      toast.success(`Subscription assigned to ${company.name}`);
      onSave();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal saas-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Subscription — {company.name}</h3>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Plan *</label>
              <select className="form-control" required value={form.plan_id} onChange={e => setForm({...form, plan_id: parseInt(e.target.value)})}>
                {plans.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name} — {fmt(p.price_monthly)}/mo</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Billing Cycle</label>
              <select className="form-control" value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trial Days</label>
              <input className="form-control" type="number" min="0" max="90" value={form.trial_days} onChange={e => setForm({...form, trial_days: parseInt(e.target.value) || 0})} />
              <small style={{ fontSize: 11, color: '#64748b' }}>Days of free trial before billing. Set to 0 for immediate billing.</small>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Assigning...' : 'Assign Subscription'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// MAIN SaaS DASHBOARD
// ============================================================
export default function SaaSDashboard() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPlanForm, setShowPlanForm] = useState(null); // null = hidden, {} = new, plan = edit
  const [assignCompany, setAssignCompany] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await subscriptionsAPI.stats()); } catch { /* */ }
  }, []);

  const loadPlans = useCallback(async () => {
    try { setPlans(await subscriptionsAPI.plans()); } catch { /* */ }
  }, []);

  const loadCompanies = useCallback(async () => {
    try { setCompanies(await subscriptionsAPI.companies({ search, status: statusFilter || undefined })); } catch { /* */ }
  }, [search, statusFilter]);

  const loadInvoices = useCallback(async () => {
    try { setInvoices(await subscriptionsAPI.invoices()); } catch { /* */ }
  }, []);

  const loadActivity = useCallback(async () => {
    try { setActivity(await subscriptionsAPI.activity()); } catch { /* */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadStats(), loadPlans(), loadCompanies(), loadInvoices(), loadActivity()])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try { await subscriptionsAPI.deletePlan(plan.id); toast.success('Plan deleted'); loadPlans(); loadStats(); } catch (err) { toast.error(err.message); }
  };

  const handleCancel = async (subId) => {
    const reason = prompt('Cancellation reason:');
    if (reason === null) return;
    try { await subscriptionsAPI.cancel(subId, { reason }); toast.success('Subscription cancelled'); loadCompanies(); loadStats(); } catch (err) { toast.error(err.message); }
  };

  const handleReactivate = async (subId) => {
    try { await subscriptionsAPI.reactivate(subId); toast.success('Subscription reactivated'); loadCompanies(); loadStats(); } catch (err) { toast.error(err.message); }
  };

  const handleConfirm = async (subId) => {
    try { await subscriptionsAPI.confirm(subId); toast.success('Subscription confirmed and activated!'); loadCompanies(); loadStats(); loadActivity(); } catch (err) { toast.error(err.message); }
  };

  const handleReject = async (subId, companyName) => {
    const reason = prompt(`Reject subscription for ${companyName}? Enter reason (optional):`);
    if (reason === null) return;
    try { await subscriptionsAPI.reject(subId, { reason: reason || 'Rejected by admin' }); toast.success('Subscription rejected'); loadCompanies(); loadStats(); loadActivity(); } catch (err) { toast.error(err.message); }
  };

  const pendingCompanies = companies.filter(c => c.sub_status === 'pending');

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'pending', label: `Pending (${pendingCompanies.length})` },
    { key: 'companies', label: 'Companies' },
    { key: 'plans', label: 'Plans' },
    { key: 'billing', label: 'Billing' },
    { key: 'activity', label: 'Activity' },
  ];

  if (loading) return <div className="spinner" style={{ margin: '60px auto' }} />;

  return (
    <div className="saas-dash">
      <div className="toolbar">
        <h2>🏢 SaaS Management</h2>
      </div>

      {/* Tabs */}
      <div className="saas-tabs">
        {tabs.map(t => (
          <button key={t.key} className={`saas-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW ===== */}
      {tab === 'overview' && (
        <div className="saas-content">
          <div className="saas-stats-grid">
            <StatCard icon={DollarSign} label="Monthly Recurring Revenue" value={fmt(stats?.mrr)} sub="from active subscriptions" color="#16a34a" />
            <StatCard icon={Building2} label="Total Companies" value={stats?.total_companies || 0} color="#2563eb" />
            <StatCard icon={CheckCircle} label="Active Subscriptions" value={stats?.active_subscriptions || 0} color="#0d9488" />
            <StatCard icon={Clock} label="Pending Confirmation" value={stats?.pending_confirmations || 0} sub="awaiting approval" color="#f59e0b" />
            <StatCard icon={Clock} label="Trialing" value={stats?.trialing || 0} sub="in trial period" color="#d97706" />
            <StatCard icon={Ban} label="Cancelled" value={stats?.cancelled || 0} color="#dc2626" />
            <StatCard icon={TrendingUp} label="Total Revenue" value={fmt(stats?.total_revenue)} sub="all time" color="#7c3aed" />
          </div>

          {/* Plan distribution */}
          {stats?.plan_distribution?.length > 0 && (
            <div className="saas-card">
              <h3 className="saas-card-title">Plan Distribution</h3>
              <div className="saas-plan-dist">
                {stats.plan_distribution.map((p, i) => {
                  const total = stats.plan_distribution.reduce((s, x) => s + x.count, 0) || 1;
                  const pct = Math.round((p.count / total) * 100);
                  const colors = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed'];
                  return (
                    <div key={i} className="saas-dist-item">
                      <div className="saas-dist-bar">
                        <div className="saas-dist-fill" style={{ width: `${pct}%`, background: colors[i % colors.length] }} />
                      </div>
                      <div className="saas-dist-info">
                        <span className="saas-dist-name">{p.name}</span>
                        <span className="saas-dist-count">{p.count} ({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent activity */}
          {activity.length > 0 && (
            <div className="saas-card">
              <h3 className="saas-card-title">Recent Activity</h3>
              <div className="saas-activity-list">
                {activity.slice(0, 10).map((a, i) => (
                  <div key={i} className="saas-activity-item">
                    <Activity size={13} className="saas-activity-icon" />
                    <div className="saas-activity-text">
                      <strong>{a.company_name}</strong> — {a.action}
                      {a.performed_by_name && <span className="saas-activity-by"> by {a.performed_by_name}</span>}
                    </div>
                    <span className="saas-activity-time">{fmtDate(a.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== PENDING CONFIRMATIONS ===== */}
      {tab === 'pending' && (
        <div className="saas-content">
          {pendingCompanies.length === 0 ? (
            <div className="saas-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CheckCircle size={36} style={{ color: '#16a34a', margin: '0 auto 12px', display: 'block' }} />
              <h3 style={{ margin: '0 0 6px', fontSize: 15, color: '#1e293b' }}>No Pending Confirmations</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>All new company registrations have been reviewed.</p>
            </div>
          ) : (
            <>
              <div className="saas-card" style={{ marginBottom: 12, background: '#fffbeb', border: '1px solid #fde68a' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#92400e' }}>
                  <Clock size={16} />
                  <strong>{pendingCompanies.length} company registration{pendingCompanies.length > 1 ? 's' : ''} awaiting confirmation.</strong>
                  New companies get 14-day free trial while pending approval.
                </div>
              </div>

              <div className="saas-table-wrap">
                <table className="saas-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Selected Plan</th>
                      <th>Billing</th>
                      <th>Registered</th>
                      <th>Trial Ends</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingCompanies.map(co => {
                      const trialEnd = co.trial_ends_at ? new Date(co.trial_ends_at) : null;
                      const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 14;
                      return (
                        <tr key={co.id}>
                          <td>
                            <div className="saas-company-cell">
                              <strong>{co.name}</strong>
                              <span className="saas-company-email">{co.email}</span>
                            </div>
                          </td>
                          <td><span className="saas-plan-badge">{co.plan_name || 'No Plan'}</span></td>
                          <td>{co.billing_cycle || '—'}</td>
                          <td>{fmtDate(co.created_at)}</td>
                          <td>
                            <span style={{ fontSize: 12, color: daysLeft <= 3 ? '#dc2626' : '#64748b' }}>
                              {fmtDate(co.trial_ends_at)}
                              <span style={{ display: 'block', fontSize: 10, fontWeight: 600, color: daysLeft <= 3 ? '#dc2626' : '#d97706' }}>
                                {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                              </span>
                            </span>
                          </td>
                          <td className="text-right">
                            <div className="saas-row-actions">
                              <button type="button" className="btn btn-primary btn-sm" onClick={() => handleConfirm(co.sub_id)}>
                                <CheckCircle size={12} /> Confirm
                              </button>
                              <button type="button" className="btn btn-danger btn-sm" onClick={() => handleReject(co.sub_id, co.name)}>
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== COMPANIES ===== */}
      {tab === 'companies' && (
        <div className="saas-content">
          <div className="saas-toolbar">
            <input className="form-control" placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
            <select className="form-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 150 }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="pending">Pending</option>
              <option value="past_due">Past Due</option>
              <option value="cancelled">Cancelled</option>
              <option value="none">No Subscription</option>
            </select>
          </div>

          <div className="saas-table-wrap">
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Billing</th>
                  <th>Users</th>
                  <th>Invoices (mo)</th>
                  <th>Period</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 && (
                  <tr><td colSpan={8} className="saas-empty">No companies found</td></tr>
                )}
                {companies.map(co => {
                  const st = STATUS_COLORS[co.sub_status] || STATUS_COLORS.expired;
                  return (
                    <tr key={co.id}>
                      <td>
                        <div className="saas-company-cell">
                          <strong>{co.name}</strong>
                          <span className="saas-company-email">{co.email}</span>
                        </div>
                      </td>
                      <td><span className="saas-plan-badge">{co.plan_name || 'No Plan'}</span></td>
                      <td>
                        {co.sub_status ? (
                          <span className="saas-status-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                        ) : (
                          <span className="saas-status-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>None</span>
                        )}
                      </td>
                      <td>{co.billing_cycle || '—'}</td>
                      <td>{co.active_users}/{co.max_users || '—'}</td>
                      <td>{co.invoices_this_month}/{co.max_invoices_month === -1 ? '∞' : co.max_invoices_month || '—'}</td>
                      <td className="saas-period-cell">
                        {co.current_period_end ? fmtDate(co.current_period_end) : '—'}
                        {co.trial_ends_at && co.sub_status === 'trialing' && (
                          <span className="saas-trial-badge">Trial ends {fmtDate(co.trial_ends_at)}</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="saas-row-actions">
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => setAssignCompany(co)}>
                            {co.sub_id ? 'Change' : 'Assign'}
                          </button>
                          {co.sub_status === 'active' && (
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleCancel(co.sub_id)}>Cancel</button>
                          )}
                          {(co.sub_status === 'cancelled' || co.sub_status === 'expired') && co.sub_id && (
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleReactivate(co.sub_id)}>
                              <RefreshCw size={11} /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== PLANS ===== */}
      {tab === 'plans' && (
        <div className="saas-content">
          <div className="saas-toolbar">
            <span className="saas-toolbar-title">{plans.length} Plans</span>
            <button type="button" className="btn btn-primary" onClick={() => setShowPlanForm({})}><Plus size={14} /> New Plan</button>
          </div>
          <div className="saas-plans-grid">
            {plans.map(plan => (
              <PlanCard key={plan.id} plan={plan} onEdit={(p) => setShowPlanForm(p)} onDelete={handleDeletePlan} />
            ))}
          </div>
        </div>
      )}

      {/* ===== BILLING ===== */}
      {tab === 'billing' && (
        <div className="saas-content">
          <div className="saas-card">
            <h3 className="saas-card-title">Billing History</h3>
            <div className="saas-table-wrap">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Cycle</th>
                    <th>Status</th>
                    <th>Period</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && (
                    <tr><td colSpan={7} className="saas-empty">No billing history</td></tr>
                  )}
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td><strong>{inv.company_name}</strong></td>
                      <td>{inv.plan_name}</td>
                      <td className="saas-amount">{fmtFull(inv.amount)}</td>
                      <td>{inv.billing_cycle}</td>
                      <td>
                        <span className={`saas-inv-status ${inv.status}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                      <td>{fmtDate(inv.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== ACTIVITY ===== */}
      {tab === 'activity' && (
        <div className="saas-content">
          <div className="saas-card">
            <h3 className="saas-card-title">Subscription Activity Log</h3>
            <div className="saas-activity-full">
              {activity.length === 0 && <p className="saas-empty">No activity yet</p>}
              {activity.map((a, i) => {
                const details = (() => { try { return JSON.parse(a.details || '{}'); } catch { return {}; } })();
                return (
                  <div key={i} className="saas-activity-full-item">
                    <div className="saas-activity-dot" />
                    <div className="saas-activity-content">
                      <div className="saas-activity-header">
                        <strong>{a.company_name}</strong>
                        <span className={`saas-activity-action ${a.action}`}>{a.action}</span>
                      </div>
                      {details.plan_name && <div className="saas-activity-detail">Plan: {details.plan_name}</div>}
                      {details.reason && <div className="saas-activity-detail">Reason: {details.reason}</div>}
                      <div className="saas-activity-meta">
                        {a.performed_by_name && <span>by {a.performed_by_name}</span>}
                        <span>{fmtDate(a.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showPlanForm !== null && (
        <PlanFormModal plan={showPlanForm.id ? showPlanForm : null} onClose={() => setShowPlanForm(null)} onSave={() => { setShowPlanForm(null); loadPlans(); loadStats(); }} />
      )}
      {assignCompany && (
        <AssignModal company={assignCompany} plans={plans} onClose={() => setAssignCompany(null)} onSave={() => { setAssignCompany(null); loadCompanies(); loadStats(); }} />
      )}
    </div>
  );
}
